import {
  Effect,
  pipe,
  Array,
  Context,
  Ref,
  Chunk,
  Record,
  Option,
  Console,
} from "effect";
import {
  CmcToken,
  CmcTokenMeta,
  InvalidSymbolError,
  Metadata,
  MetadataByAddress,
  MetadataBySymbol,
} from "./types";
import { isAlphanumeric, parseInvalidSymbols, wasNotFound } from "./utils";
import { request } from "./request";
import { Address, ChainKey, makeAddressSync, NotFoundError } from "@silo/types";
import { noop } from "@silo/utils";
import { TOKEN_ADDRESS } from "@silo/config";

interface RequestMetric {
  endpoint: string;
  status: number;
  credits: number;
}

class RequestsState extends Context.Tag("RequestsState")<
  RequestsState,
  Ref.Ref<Chunk.Chunk<RequestMetric>>
>() {}

class TotalCreditsState extends Context.Tag("TotalCreditsState")<
  TotalCreditsState,
  Ref.Ref<number>
>() {}

export type GetTokenMeta = ReturnType<typeof getTokenMeta>;
export type GetTokenMetaSuccess = Effect.Effect.Success<GetTokenMeta>;
export type GetTokenMetaError = Effect.Effect.Error<GetTokenMeta>;
export type GetTokenMetaContext = Effect.Effect.Context<GetTokenMeta>;

interface SearchToken {
  chainKey: ChainKey;
  tokenAddress: Address;
  symbol: string;
}

interface AdjustedSearchToken extends SearchToken {
  pricingChainKey: ChainKey;
  pricingTokenAddress: Address;
}

export function getTokenMeta(tokens: SearchToken[]) {
  return Effect.gen(function* () {
    const adjustedTokens = tokens.map(adjustSearchToken);

    /**
     * If token symbols contain non-alphanumeric symbols, the Coin Market Cap
     * API returns an error. So we must filter those out and query them
     * individually by address.
     */
    const [nonAlphanumericTokens, alphanumericTokens] = pipe(
      adjustedTokens,
      Array.partition(({ symbol: rawSymbol }) => isAlphanumeric(rawSymbol))
    );

    const { metadataBySymbol, invalidTokens } =
      yield* getMetadataBySymbolEffectWithRetry(alphanumericTokens);

    const cmcTokenMetaMap = new Map<Address, CmcTokenMeta>();

    const remainingTokens = new Set(
      [...nonAlphanumericTokens, ...invalidTokens].map(
        (token) => token.tokenAddress
      )
    );

    /**
     * need to create a new map to convert the keys to lowercase
     *
     * TODO: think of a better type safe way to do this
     */
    const metadataBySymbolMap = pipe(
      metadataBySymbol,
      Record.toEntries,
      Array.map(([key, value]) => [key.toLowerCase(), value] as const),
      (entries) => new Map(entries)
    );

    for (const {
      pricingTokenAddress,
      tokenAddress,
      symbol: rawSymbol,
    } of alphanumericTokens) {
      pipe(
        metadataBySymbolMap.get(rawSymbol.toLowerCase()),
        Option.fromNullable,
        Option.flatMap(
          Array.findFirst((meta) =>
            meta.contract_address.some(
              (d) =>
                d.contract_address.toLowerCase() ===
                pricingTokenAddress.toLowerCase()
            )
          )
        ),
        Option.map(toCmcTokenMeta),
        Option.match({
          onSome: (meta) => cmcTokenMetaMap.set(tokenAddress, meta),
          onNone: () => remainingTokens.add(tokenAddress),
        })
      );
    }

    const results = yield* Effect.forEach(
      remainingTokens,
      (tokenAddress) =>
        pipe(
          getMetadataByAddress(tokenAddress),
          Effect.catchTag("NotFoundError", () => Effect.succeed(null)),
          Effect.map((data) => ({ tokenAddress, data }))
        ),
      { concurrency: "unbounded" }
    );

    for (const { tokenAddress, data } of results) {
      pipe(
        data,
        Option.fromNullable,
        Option.map((record) => Object.values(record)),
        Option.flatMap(Array.head),
        Option.map(toCmcTokenMeta),
        Option.match({
          onSome: (meta) => cmcTokenMetaMap.set(tokenAddress, meta),
          onNone: noop,
        })
      );
    }

    const requestsState = yield* RequestsState;
    const requests = yield* pipe(
      Ref.get(requestsState),
      Effect.map(Chunk.toArray)
    );

    const totalCreditsState = yield* TotalCreditsState;
    const totalCredits = yield* Ref.get(totalCreditsState);

    const [unidentified, identified] = yield* Effect.partition(
      adjustedTokens,
      (token) => {
        const { tokenAddress } = token;

        return pipe(
          cmcTokenMetaMap.get(tokenAddress),
          Option.fromNullable,
          Option.map((meta): CmcToken => ({ ...token, meta })),
          Effect.mapError(() => token)
        );
      }
    );

    if (unidentified.length > 0) {
      yield* Effect.logWarning(
        `CoinMarketCap count of unidentified tokens: ${unidentified.length}`
      );
      yield* Console.log("Unidentified tokens:", unidentified);
    }

    return {
      identified,
      unidentified,
      metrics: {
        requests,
        totalCredits,
      },
    };
  }).pipe(
    Effect.provideServiceEffect(RequestsState, Ref.make(Chunk.empty())),
    Effect.provideServiceEffect(TotalCreditsState, Ref.make(0))
  );
}

function getMetadataBySymbolEffectWithRetry(tokens: AdjustedSearchToken[]) {
  return pipe(
    getMetadataBySymbol(tokens),
    Effect.matchEffect({
      onSuccess: (metadataBySymbol) => {
        const invalidTokens: AdjustedSearchToken[] = [];

        return Effect.succeed({ metadataBySymbol, invalidTokens });
      },
      onFailure: (error) => {
        if (!(error instanceof InvalidSymbolError)) {
          return Effect.fail(error);
        }

        const [validTokens, invalidTokens] = pipe(
          tokens,
          Array.partition(({ symbol: rawSymbol }) =>
            error.invalidSymbols.includes(rawSymbol.toLowerCase())
          )
        );

        return pipe(
          getMetadataBySymbol(validTokens),
          Effect.map((metadataBySymbol) => ({
            metadataBySymbol,
            invalidTokens,
          }))
        );
      },
    })
  );
}

export type GetMetadataBySymbol = ReturnType<typeof getMetadataBySymbol>;
export type GetMetadataBySymbolSuccess =
  Effect.Effect.Success<GetMetadataBySymbol>;
export type GetMetadataBySymbolError = Effect.Effect.Error<GetMetadataBySymbol>;
export type GetMetadataBySymbolContext =
  Effect.Effect.Context<GetMetadataBySymbol>;

export function getMetadataBySymbol(tokens: AdjustedSearchToken[]) {
  return Effect.gen(function* () {
    if (tokens.length === 0) {
      const data: MetadataBySymbol = {};

      return data;
    }

    const symbols = tokens.map((token) => token.symbol).join(",");

    const endpoint = `/v2/cryptocurrency/info?symbol=${symbols}` as const;

    const { data, status, credits } = yield* pipe(
      request(endpoint, MetadataBySymbol),
      Effect.mapError((error) => {
        if (error._tag !== "ClientError") return error;

        const invalidSymbols = parseInvalidSymbols(error.message);

        return invalidSymbols
          ? new InvalidSymbolError({ invalidSymbols })
          : error;
      })
    );

    const requestsState = yield* RequestsState;

    yield* Ref.update(
      requestsState,
      Chunk.append({
        endpoint,
        status,
        credits,
      })
    );

    const totalCreditsState = yield* TotalCreditsState;
    yield* Ref.update(totalCreditsState, (n) => n + credits);

    return data;
  });
}

function toCmcTokenMeta({ id, platform, logo }: Metadata): CmcTokenMeta {
  return {
    id,
    logo,
    platform: pipe(
      platform,
      Option.fromNullable,

      Option.flatMapNullable(({ id, token_address }) => {
        // TODO: safe-address-constructor
        try {
          return {
            id,
            tokenAddress: makeAddressSync(token_address),
          };
        } catch (error) {
          return null;
        }
      }),
      Option.getOrNull
    ),
  };
}

export type GetMetadataByAddress = ReturnType<typeof getMetadataByAddress>;
export type GetMetadataByAddressSuccess =
  Effect.Effect.Success<GetMetadataByAddress>;
export type GetMetadataByAddressError =
  Effect.Effect.Error<GetMetadataByAddress>;
export type GetMetadataByAddressContext =
  Effect.Effect.Context<GetMetadataByAddress>;

export function getMetadataByAddress(tokenAddress: Address) {
  return Effect.gen(function* () {
    const endpoint = `/v2/cryptocurrency/info?address=${tokenAddress}` as const;

    const { data, status, credits } = yield* pipe(
      request(endpoint, MetadataByAddress),
      Effect.mapError((error) => {
        if (error._tag !== "ClientError") return error;

        const { message, status, code } = error;

        return wasNotFound(error.message)
          ? new NotFoundError({ message, status, code })
          : error;
      })
    );

    const requestsState = yield* RequestsState;

    yield* Ref.update(
      requestsState,
      Chunk.append({
        endpoint,
        status,
        credits,
      })
    );

    const totalCreditsState = yield* TotalCreditsState;
    yield* Ref.update(totalCreditsState, (n) => n + credits);

    return data;
  });
}

/**
 * TODO: think of a better way to handle this situation
 */

function adjustSearchToken(token: SearchToken): AdjustedSearchToken {
  const { chainKey, tokenAddress } = token;

  if (chainKey === "base" && tokenAddress === TOKEN_ADDRESS.base.SILO) {
    return {
      ...token,
      pricingChainKey: "ethereum",
      pricingTokenAddress: TOKEN_ADDRESS.ethereum.SILO,
    };
  }
  if (chainKey === "optimism" && tokenAddress === TOKEN_ADDRESS.optimism.SILO) {
    return {
      ...token,
      pricingChainKey: "ethereum",
      pricingTokenAddress: TOKEN_ADDRESS.ethereum.SILO,
    };
  }

  if (
    chainKey === "optimism" &&
    tokenAddress ===
      makeAddressSync("0x67CCEA5bb16181E7b4109c9c2143c24a1c2205Be")
  ) {
    return {
      ...token,
      pricingChainKey: "ethereum",
      pricingTokenAddress: TOKEN_ADDRESS.ethereum.FXS,
    };
  }

  return {
    ...token,
    pricingChainKey: chainKey,
    pricingTokenAddress: tokenAddress,
  };
}
